import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { makeSseParser } from "@/lib/sse";
import PersonaBadge, { PERSONA_ICONS } from "@/components/PersonaBadge";

export default function PanelCoach({ ideaId, personaId, onClose }) {
  const [persona, setPersona] = useState(null);
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const [streaming, setStreaming] = useState(""); // live partial assistant text
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coachStyle, setCoachStyle] = useState("blunt");
  const [coachStyleDefault, setCoachStyleDefault] = useState("blunt");
  const [coachStyleSource, setCoachStyleSource] = useState("auto"); // 'auto' | 'override'
  const [styleMenuOpen, setStyleMenuOpen] = useState(false);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/ideas/${ideaId}/coach/${personaId}`);
        if (cancelled) return;
        setPersona(r.data.persona);
        setTurns(r.data.turns || []);
        setCoachStyle(r.data.coach_style || "blunt");
        setCoachStyleDefault(r.data.coach_style_default || "blunt");
        setCoachStyleSource(r.data.coach_style_source || "auto");
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load coach.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [ideaId, personaId]);

  async function changeCoachStyle(nextStyle) {
    // nextStyle === null resets to auto-matched default
    setStyleMenuOpen(false);
    try {
      const r = await api.patch(`/ideas/${ideaId}/coach/${personaId}/style`, {
        coach_style: nextStyle,
      });
      setCoachStyle(r.data.coach_style);
      setCoachStyleDefault(r.data.coach_style_default);
      setCoachStyleSource(r.data.coach_style_source);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not change coach style.");
    }
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, streaming, sending]);

  async function send(e) {
    e.preventDefault();
    if (!question.trim() || sending) return;
    setSending(true);
    setError(null);
    setStreaming("");
    const q = question.trim();
    setQuestion("");
    const optimisticId = `tmp-${Date.now()}`;
    setTurns((prev) => [
      ...prev,
      {
        id: optimisticId, role: "user", content: q,
        persona_id: personaId, idea_id: ideaId,
        created_at: new Date().toISOString(),
      },
    ]);

    const ac = new AbortController();
    abortRef.current = ac;
    const parse = makeSseParser();
    let finalAssistant = null;

    try {
      const resp = await fetch(`${API}/ideas/${ideaId}/coach/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ persona_id: personaId, question: q }),
        signal: ac.signal,
      });
      if (!resp.ok) {
        let detail = "Coach did not respond.";
        try {
          const j = await resp.json();
          detail = j?.detail || detail;
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let assembled = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunkText = decoder.decode(value, { stream: true });
        for (const frame of parse(chunkText)) {
          if (frame.event === "open") {
            // Server has persisted the founder's turn — swap the optimistic one in place.
            setTurns((prev) => prev.map((t) => (t.id === optimisticId ? frame.data.user_turn : t)));
          } else if (frame.event === "delta") {
            assembled += frame.data.text || "";
            setStreaming(assembled);
          } else if (frame.event === "done") {
            finalAssistant = frame.data.assistant_turn;
          } else if (frame.event === "error") {
            throw new Error(frame.data.detail || "Coach stream errored.");
          }
        }
      }
      if (finalAssistant) {
        setTurns((prev) => [...prev, finalAssistant]);
      }
      setStreaming("");
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e?.message || "Coach did not respond. Try again.");
        // Roll back optimistic turn if we bailed before the server ack'd
        setTurns((prev) => prev.filter((t) => t.id !== optimisticId));
      }
      setStreaming("");
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }

  const color = persona?.color || "#334155";
  const Icon = PERSONA_ICONS[persona?.icon];
  const COACH_STYLE_LABELS = { warm: "Warm", blunt: "Blunt", socratic: "Socratic" };
  const COACH_STYLE_ORDER = ["warm", "blunt", "socratic"];

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="panel-coach-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white shadow-2xl h-full flex flex-col">
        <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between" style={{ borderLeft: `4px solid ${color}` }}>
          <div className="min-w-0">
            <div className="mono-caps mb-1 flex items-center gap-2" style={{ color }} data-testid="coach-persona-tag">
              {Icon && <Icon size={12} strokeWidth={2.4} />}
              Coaching · {personaId}
            </div>
            <h2 className="font-display text-2xl leading-tight truncate">
              {persona ? persona.name : "…"}
            </h2>
            {persona && (
              <div className="mono-caps text-slate-400 text-[0.65rem] mt-1">
                {persona.stage === "angel" ? "Angel" : "Seed"} · {persona.check_size}
              </div>
            )}
            {/* Coach-style badge + switcher: auto-matches to persona stage
                (angel → warm, seed → blunt) unless founder overrides it. */}
            {!loading && (
              <div className="relative mt-3 inline-block" data-testid="coach-style-switcher">
                <button
                  type="button"
                  onClick={() => setStyleMenuOpen((v) => !v)}
                  className="mono-caps text-[0.6rem] px-2 py-1 rounded-md bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200"
                  data-testid="coach-style-switcher-btn"
                >
                  Coach: {COACH_STYLE_LABELS[coachStyle] || coachStyle}
                  <span className="ml-1.5 text-[0.5rem] px-1 py-[1px] rounded"
                        style={{
                          backgroundColor: coachStyleSource === "auto" ? "#DBEAFE" : "#FED7AA",
                          color: coachStyleSource === "auto" ? "#1E40AF" : "#9A3412",
                        }}
                        data-testid="coach-style-source">
                    {coachStyleSource === "auto" ? "AUTO" : "MANUAL"}
                  </span>
                  <span className="ml-1 text-slate-400">▾</span>
                </button>
                {styleMenuOpen && (
                  <div className="absolute left-0 mt-1 w-48 bg-white border border-slate-200 rounded-md shadow-lg z-20 overflow-hidden">
                    {COACH_STYLE_ORDER.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => changeCoachStyle(s)}
                        className={`w-full text-left mono-caps text-[0.65rem] px-3 py-2 hover:bg-slate-50 ${
                          s === coachStyle ? "bg-slate-50 font-semibold text-slate-900" : "text-slate-700"
                        }`}
                        data-testid={`coach-style-switch-${s}`}
                      >
                        {COACH_STYLE_LABELS[s]}
                        {s === coachStyleDefault && (
                          <span className="ml-2 text-[0.55rem] text-blue-600">default</span>
                        )}
                        {s === coachStyle && <span className="ml-2 text-emerald-600">✓</span>}
                      </button>
                    ))}
                    {coachStyleSource === "override" && (
                      <button
                        type="button"
                        onClick={() => changeCoachStyle(null)}
                        className="w-full text-left mono-caps text-[0.6rem] px-3 py-2 border-t border-slate-100 text-blue-600 hover:bg-blue-50"
                        data-testid="coach-style-reset"
                      >
                        Reset to auto ({COACH_STYLE_LABELS[coachStyleDefault]})
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            className="mono-caps text-slate-500 hover:text-slate-900 shrink-0 ml-4"
            onClick={onClose}
            data-testid="coach-close-btn"
          >
            Close ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {loading ? (
            <div className="text-slate-500">Loading…</div>
          ) : (
            <>
              {persona?.voice_preview && (
                <blockquote
                  className="text-sm text-slate-700 italic leading-snug border-l-2 pl-4 py-1"
                  style={{ borderColor: color }}
                  data-testid="coach-voice-preview"
                >
                  "{persona.voice_preview}"
                </blockquote>
              )}
              {turns.length === 0 && !streaming ? (
                <div className="text-slate-500 text-sm">
                  Ask <PersonaBadge persona={persona} size="sm" /> a follow-up
                  about their panel verdict on your plan. They'll answer in their voice.
                </div>
              ) : (
                turns.map((t) => (
                  <div
                    key={t.id}
                    className={t.role === "user" ? "chat-message user" : "chat-message assistant"}
                    data-testid={`coach-turn-${t.role}`}
                  >
                    <div className="mono-caps mb-2" style={{ color: t.role === "user" ? "#5B6472" : color }}>
                      {t.role === "user" ? "Founder" : `${personaId} · ${persona?.name || ""}`}
                    </div>
                    <div className={t.role === "user" ? "text-slate-900" : "text-slate-800 whitespace-pre-wrap leading-relaxed"}>
                      {t.content}
                    </div>
                  </div>
                ))
              )}
            </>
          )}
          {streaming && (
            <div className="chat-message assistant" data-testid="coach-streaming-turn">
              <div className="mono-caps mb-2" style={{ color }}>
                {personaId} · {persona?.name || ""}
              </div>
              <div className="text-slate-800 whitespace-pre-wrap leading-relaxed">
                {streaming}
                <span
                  className="inline-block w-2 h-4 ml-1 align-middle animate-pulse"
                  style={{ backgroundColor: color }}
                />
              </div>
            </div>
          )}
          {sending && !streaming && (
            <div className="mono-caps text-slate-400 animate-pulse" data-testid="coach-thinking" style={{ color }}>
              {personaId} thinking…
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={send} className="px-6 py-5 border-t border-slate-200">
          {error && (
            <div className="text-red-700 text-sm mb-3" data-testid="coach-error">{error}</div>
          )}
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={`Ask ${personaId} about their verdict…`}
            className="input-line"
            rows={3}
            disabled={sending}
            data-testid="coach-input"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") send(e);
            }}
          />
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="mono-caps text-slate-400 text-[0.65rem]">⌘ + ↵ to send</span>
            <button
              type="submit"
              className="btn-primary"
              disabled={sending || !question.trim()}
              data-testid="coach-send"
              style={sending ? undefined : { backgroundColor: color }}
            >
              {sending ? "Sending…" : "Ask"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
